<script lang="ts">
    import { useForm } from '@inertiajs/svelte';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import Select from '@/components/Inputs/Select.svelte';
    import * as Card from '@/components/ui/card';
    import { Label } from '@/components/ui/label';
    import InputError from '@/components/InputError.svelte';
    import { Toaster, toast } from 'svelte-sonner';
    import { onMount } from 'svelte';

    type Props = {
        purposeOptions: string[];
        complexAccessOptions: string[];
        enquirerTypeOptions: { value: string; label: string }[];
        sourceOptions: string[];
        recaptchaSiteKey: string | null;
        apiBaseUrl: string;
        submitted?: boolean;
        errorMessage?: string | null;
    };

    let {
        purposeOptions,
        complexAccessOptions,
        enquirerTypeOptions,
        sourceOptions,
        recaptchaSiteKey,
        apiBaseUrl,
        submitted = false,
        errorMessage = null,
    }: Props = $props();

    // Convert string arrays to options format
    const purposeSelectOptions = $derived(
        purposeOptions.map((opt) => ({ value: opt, label: opt }))
    );
    const sourceSelectOptions = $derived(
        sourceOptions.map((opt) => ({ value: opt, label: opt }))
    );

    const form = useForm({
        name: '',
        email: '',
        phone: '',
        address_line_one: '',
        address_line_two: '',
        city: '',
        county: '',
        postcode: '',
        country: 'United Kingdom',
        date_needed: '',
        purpose: '',
        complex_access_needs: [] as string[],
        enquirer_type: '',
        comments: '',
        source: '',
        photos: [] as File[],
        gdpr_consent: false,
        recaptcha_token: '',
    });

    // Address autocomplete state
    let addressInput = $state('');
    let addressSuggestions = $state<{ description: string; place_id: string }[]>([]);
    let showSuggestions = $state(false);
    let debounceTimer: ReturnType<typeof setTimeout>;

    // reCAPTCHA state
    let recaptchaLoaded = $state(false);

    // Success state
    let showSuccess = $state(false);

    // File input ref
    let fileInput: HTMLInputElement;

    onMount(() => {
        // Load reCAPTCHA script
        if (recaptchaSiteKey) {
            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);

            (window as any).onRecaptchaLoad = () => {
                recaptchaLoaded = true;
                renderRecaptcha();
            };
        }
    });

    // The submission flow is stateless (the server returns this page with `submitted: true`
    // rather than a session redirect) so it works inside cross-origin iframes. React to that
    // prop to show success and notify the parent page for Google Tag Manager.
    let hasNotified = false;
    $effect(() => {
        if (submitted && !hasNotified) {
            hasNotified = true;
            showSuccess = true;
            notifyParentOfSubmission();
        }
    });

    $effect(() => {
        if (errorMessage) {
            toast.error(errorMessage);
        }
    });

    function renderRecaptcha() {
        if (!recaptchaLoaded || !recaptchaSiteKey) return;

        const container = document.getElementById('recaptcha-container');
        if (!container) return;

        (window as any).grecaptcha.render(container, {
            sitekey: recaptchaSiteKey,
            callback: (token: string) => {
                $form.recaptcha_token = token;
            },
            'expired-callback': () => {
                $form.recaptcha_token = '';
            },
        });
    }

    // Notify the parent page (when embedded in a cross-origin iframe) that a lead was
    // submitted, so its Google Tag Manager dataLayer can fire a conversion. The browser's
    // same-origin policy prevents GTM from observing events inside the iframe directly.
    function notifyParentOfSubmission() {
        if (window.parent !== window) {
            window.parent.postMessage(
                { event: 'culmex_iframe_form_submit', formName: 'bespoke_quote_form' },
                '*'
            );
        }
    }

    async function searchAddress(query: string) {
        if (query.length < 3) {
            addressSuggestions = [];
            return;
        }

        try {
            const response = await fetch(
                `${apiBaseUrl}/api/places/autocomplete?input=${encodeURIComponent(query)}`
            );
            const data = await response.json();
            addressSuggestions = data.predictions || [];
            showSuggestions = addressSuggestions.length > 0;
        } catch (error) {
            console.error('Address search failed:', error);
            addressSuggestions = [];
        }
    }

    function handleAddressInput(e: Event) {
        const target = e.target as HTMLInputElement;
        addressInput = target.value;

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            searchAddress(addressInput);
        }, 300);
    }

    async function selectAddress(suggestion: { description: string; place_id: string }) {
        showSuggestions = false;
        addressInput = suggestion.description;

        try {
            const response = await fetch(
                `${apiBaseUrl}/api/places/details?place_id=${encodeURIComponent(suggestion.place_id)}`
            );
            const data = await response.json();

            if (data.address) {
                $form.address_line_one = data.address.address_line_one || '';
                $form.address_line_two = data.address.address_line_two || '';
                $form.city = data.address.city || '';
                $form.county = data.address.county || '';
                $form.postcode = data.address.postcode || '';
                $form.country = data.address.country || 'United Kingdom';
            }
        } catch (error) {
            console.error('Failed to get address details:', error);
        }
    }

    function handleComplexAccessChange(option: string, checked: boolean) {
        if (checked) {
            $form.complex_access_needs = [...$form.complex_access_needs, option];
        } else {
            $form.complex_access_needs = $form.complex_access_needs.filter((o) => o !== option);
        }
    }

    function handleFileChange(e: Event) {
        const target = e.target as HTMLInputElement;
        const files = Array.from(target.files || []);

        // Limit to 10 files
        if (files.length > 10) {
            toast.error('You can upload a maximum of 10 photos');
            return;
        }

        // Check file sizes (10MB max each)
        const oversizedFiles = files.filter((f) => f.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            toast.error('Each photo must be less than 10MB');
            return;
        }

        $form.photos = files;
    }

    // Validate required fields client-side and surface inline errors. Server validation
    // errors are session-backed and so never reach a cross-origin iframe, so this is the
    // only feedback the user gets for missing fields. The server still validates as the
    // authoritative backstop.
    const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function validateForm(): boolean {
        $form.clearErrors();
        let valid = true;

        if (!$form.name.trim()) {
            $form.setError('name', 'Please enter your name.');
            valid = false;
        }

        if (!$form.email.trim()) {
            $form.setError('email', 'Please enter your email address.');
            valid = false;
        } else if (!EMAIL_PATTERN.test($form.email)) {
            $form.setError('email', 'Please enter a valid email address.');
            valid = false;
        }

        if (!$form.phone.trim()) {
            $form.setError('phone', 'Please enter your phone number.');
            valid = false;
        }

        if (!$form.address_line_one.trim()) {
            $form.setError('address_line_one', 'Please enter your address.');
            valid = false;
        }

        if (!$form.city.trim()) {
            $form.setError('city', 'Please enter your city or town.');
            valid = false;
        }

        if (!$form.postcode.trim()) {
            $form.setError('postcode', 'Please enter your postcode.');
            valid = false;
        }

        return valid;
    }

    // Scroll to and focus the first field with a validation error. Field ids match the
    // form keys, and this order follows the visual order of the fields.
    function focusFirstError(): void {
        const order = ['name', 'email', 'phone', 'address_line_one', 'city', 'postcode'];
        const errors = $form.errors as Record<string, string>;
        const firstField = order.find((field) => errors[field]);
        if (!firstField) {
            return;
        }
        const el = document.getElementById(firstField);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus({ preventScroll: true });
    }

    function submit(e: Event) {
        e.preventDefault();

        if (!validateForm()) {
            focusFirstError();
            return;
        }

        if (!$form.gdpr_consent) {
            toast.error('You must consent to data processing to submit this form');
            return;
        }

        if (!$form.recaptcha_token && recaptchaSiteKey) {
            toast.error('Please complete the reCAPTCHA verification');
            return;
        }

        // Success is handled via the `submitted` prop returned by the server (see the
        // $effect above), which keeps the flow stateless for cross-origin iframe embedding.
        $form.post(route('embed.contact-form.store'), {
            forceFormData: true,
            onError: (errors) => {
                if (errors.general) {
                    toast.error(errors.general);
                }
            },
        });
    }

    function submitAnother() {
        // Reload to a fresh form so a second submission re-triggers the success state.
        window.location.reload();
    }
</script>

<svelte:head>
    <title>Contact Us - Culmex Scaffolding</title>
    <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap"
        rel="stylesheet"
    />
</svelte:head>

<Toaster position="top-right" richColors closeButton />

<div class="culmex-embed">
    <div class="culmex-container">
        <!-- Header -->
        <div class="culmex-header">
            <img src="/culmex-logo.svg" alt="Culmex Scaffolding" class="culmex-logo" />
            <h1 class="culmex-title">Request a Quote</h1>
            <p class="culmex-subtitle">Fill out the form below and we'll be in touch shortly.</p>
        </div>

        {#if showSuccess}
            <!-- Success Message -->
            <div class="culmex-card">
                <div class="culmex-success">
                    <svg class="culmex-success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <h2 class="culmex-success-title">Thank You!</h2>
                    <p class="culmex-success-text">
                        Your enquiry has been submitted successfully. We will be in touch soon.
                    </p>
                    <button class="culmex-btn-outline" onclick={submitAnother}>
                        Submit Another Enquiry
                    </button>
                </div>
            </div>
        {:else}
            <form onsubmit={submit} class="culmex-form">

                <!-- Contact Information -->
                <div class="culmex-card">
                    <div class="culmex-card-header">Contact Information</div>
                    <div class="culmex-card-body">
                        <div class="culmex-field">
                            <label class="culmex-label" for="name">Name <span class="culmex-required">*</span></label>
                            <Input id="name" type="text" bind:value={$form.name} placeholder="Enter your full name" maxlength={255} class="culmex-input" />
                            <InputError message={$form.errors.name} />
                        </div>
                        <div class="culmex-field">
                            <label class="culmex-label" for="email">Email <span class="culmex-required">*</span></label>
                            <Input id="email" type="email" bind:value={$form.email} placeholder="your@email.com" class="culmex-input" />
                            <InputError message={$form.errors.email} />
                        </div>
                        <div class="culmex-field">
                            <label class="culmex-label" for="phone">Phone <span class="culmex-required">*</span></label>
                            <Input id="phone" type="tel" bind:value={$form.phone} placeholder="Enter your phone number" class="culmex-input" />
                            <InputError message={$form.errors.phone} />
                        </div>
                    </div>
                </div>

                <!-- Address -->
                <div class="culmex-card">
                    <div class="culmex-card-header">
                        Address <span class="culmex-required">*</span>
                    </div>
                    <div class="culmex-card-body">
                        <p class="culmex-hint">Enter postcode or start typing your address and select from the dropdown.</p>
                        <div class="culmex-field" style="position:relative">
                            <label class="culmex-label" for="address_search">Search for a location</label>
                            <Input
                                id="address_search"
                                type="text"
                                value={addressInput}
                                oninput={handleAddressInput}
                                onfocus={() => (showSuggestions = addressSuggestions.length > 0)}
                                onblur={() => setTimeout(() => (showSuggestions = false), 200)}
                                placeholder="Start typing your address..."
                                autocomplete="off"
                                class="culmex-input"
                            />
                            {#if showSuggestions && addressSuggestions.length > 0}
                                <div class="culmex-suggestions">
                                    {#each addressSuggestions as suggestion}
                                        <button
                                            type="button"
                                            class="culmex-suggestion-item"
                                            onmousedown={() => selectAddress(suggestion)}
                                        >
                                            {suggestion.description}
                                        </button>
                                    {/each}
                                </div>
                            {/if}
                        </div>

                        <div class="culmex-grid">
                            <div class="culmex-field culmex-col-2">
                                <label class="culmex-label" for="address_line_one">Address Line 1</label>
                                <Input id="address_line_one" type="text" bind:value={$form.address_line_one} class="culmex-input" />
                                <InputError message={$form.errors.address_line_one} />
                            </div>
                            <div class="culmex-field culmex-col-2">
                                <label class="culmex-label" for="address_line_two">Address Line 2</label>
                                <Input id="address_line_two" type="text" bind:value={$form.address_line_two} class="culmex-input" />
                            </div>
                            <div class="culmex-field">
                                <label class="culmex-label" for="city">City / Town</label>
                                <Input id="city" type="text" bind:value={$form.city} class="culmex-input" />
                                <InputError message={$form.errors.city} />
                            </div>
                            <div class="culmex-field">
                                <label class="culmex-label" for="county">County</label>
                                <Input id="county" type="text" bind:value={$form.county} class="culmex-input" />
                            </div>
                            <div class="culmex-field">
                                <label class="culmex-label" for="postcode">Postcode</label>
                                <Input id="postcode" type="text" bind:value={$form.postcode} class="culmex-input" />
                                <InputError message={$form.errors.postcode} />
                            </div>
                            <div class="culmex-field">
                                <label class="culmex-label" for="country">Country</label>
                                <Input id="country" type="text" bind:value={$form.country} class="culmex-input" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Project Details -->
                <div class="culmex-card">
                    <div class="culmex-card-header">Project Details</div>
                    <div class="culmex-card-body">
                        <div class="culmex-field">
                            <label class="culmex-label" for="date_needed">Date Scaffolding Needed</label>
                            <Input id="date_needed" type="date" bind:value={$form.date_needed} class="culmex-input" />
                        </div>

                        <Select
                            label="Purpose of Scaffolding"
                            bind:value={$form.purpose}
                            options={purposeSelectOptions}
                            error={$form.errors.purpose}
                        />

                        <div class="culmex-field">
                            <label class="culmex-label">
                                Do you have any complex access needs where the scaffolding will be erected?
                            </label>
                            <div class="culmex-checkboxes">
                                {#each complexAccessOptions as option}
                                    <Checkbox
                                        label={option}
                                        checked={$form.complex_access_needs.includes(option)}
                                        onCheckedChange={(checked) => handleComplexAccessChange(option, checked)}
                                    />
                                {/each}
                            </div>
                        </div>

                        <Select
                            label="Please select which of the following applies"
                            bind:value={$form.enquirer_type}
                            options={enquirerTypeOptions}
                            error={$form.errors.enquirer_type}
                        />

                        <Textarea
                            label="Comments"
                            bind:value={$form.comments}
                            maxlength={2000}
                            error={$form.errors.comments}
                        />
                    </div>
                </div>

                <!-- Additional Information -->
                <div class="culmex-card">
                    <div class="culmex-card-header">Additional Information</div>
                    <div class="culmex-card-body">
                        <Select
                            label="How did you find us?"
                            bind:value={$form.source}
                            options={sourceSelectOptions}
                            error={$form.errors.source}
                        />

                        <div class="culmex-field">
                            <label class="culmex-label" for="photos">
                                Please upload any photos of where you need the scaffolding
                            </label>
                            <p class="culmex-hint">Maximum 10 photos, 10MB each</p>
                            <input
                                bind:this={fileInput}
                                id="photos"
                                type="file"
                                accept="image/*"
                                multiple
                                onchange={handleFileChange}
                                class="culmex-file-input"
                            />
                            {#if $form.photos.length > 0}
                                <p class="culmex-hint">{$form.photos.length} file(s) selected</p>
                            {/if}
                            <InputError message={$form.errors.photos} />
                        </div>
                    </div>
                </div>

                <!-- Consent & Submit -->
                <div class="culmex-card">
                    <div class="culmex-card-body">
                        <div class="culmex-field">
                            <div class="culmex-consent-row">
                                <input
                                    type="checkbox"
                                    id="gdpr_consent"
                                    bind:checked={$form.gdpr_consent}
                                    class="culmex-native-checkbox"
                                />
                                <div class="culmex-consent-text">
                                    <label for="gdpr_consent" class="culmex-consent-title">
                                        I consent to Culmex Construction storing and processing my information.
                                        <span class="culmex-required">*</span>
                                    </label>
                                    <p class="culmex-hint">
                                        By submitting this form you consent to us storing and processing your
                                        personal data according to our
                                        <a
                                            href="https://culmexconstruction.co.uk/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="culmex-link"
                                        >Privacy Policy</a>.
                                    </p>
                                </div>
                            </div>
                            <InputError message={$form.errors.gdpr_consent} />
                        </div>

                        {#if recaptchaSiteKey}
                            <div class="culmex-field">
                                <div id="recaptcha-container"></div>
                                <InputError message={$form.errors.recaptcha_token} />
                            </div>
                        {/if}

                        <div class="culmex-submit-row">
                            <button type="submit" class="culmex-btn-primary" disabled={$form.processing}>
                                {$form.processing ? 'Submitting...' : 'Submit Enquiry'}
                            </button>
                        </div>
                    </div>
                </div>

            </form>
        {/if}
    </div>
</div>

<style>
    .culmex-embed {
        font-family: 'Open Sans', sans-serif;
        background: #f5f5f5;
        min-height: 100vh;
        padding: 2rem 1rem;
        color: #333;
    }

    .culmex-container {
        max-width: 680px;
        margin: 0 auto;
    }

    /* Header */
    .culmex-header {
        text-align: center;
        margin-bottom: 2rem;
    }

    .culmex-logo {
        height: 48px;
        margin: 0 auto 1rem;
        display: block;
    }

    .culmex-title {
        font-size: 1.75rem;
        font-weight: 700;
        color: #1e3060;
        margin: 0 0 0.5rem;
        letter-spacing: -0.01em;
    }

    .culmex-subtitle {
        color: #666;
        font-size: 0.95rem;
        margin: 0;
    }

    /* Cards */
    .culmex-card {
        background: #fff;
        border: 1px solid #ddd;
        margin-bottom: 1.25rem;
    }

    .culmex-card-header {
        background: #1e3060;
        color: #fff;
        font-size: 0.85rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 0.75rem 1.25rem;
    }

    .culmex-card-body {
        padding: 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    /* Fields */
    .culmex-field {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    .culmex-label {
        font-size: 0.85rem;
        font-weight: 600;
        color: #1e3060;
        letter-spacing: 0.01em;
    }

    .culmex-required {
        color: #c0392b;
    }

    .culmex-hint {
        font-size: 0.8rem;
        color: #777;
        margin: 0;
    }

    /* Grid layout for address */
    .culmex-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.75rem;
    }

    .culmex-col-2 {
        grid-column: span 2;
    }

    /* Address suggestions */
    .culmex-suggestions {
        position: absolute;
        z-index: 10;
        width: 100%;
        margin-top: 2px;
        background: #fff;
        border: 1px solid #ddd;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        max-height: 240px;
        overflow-y: auto;
    }

    .culmex-suggestion-item {
        width: 100%;
        padding: 0.6rem 1rem;
        text-align: left;
        font-size: 0.875rem;
        background: none;
        border: none;
        cursor: pointer;
        color: #333;
        border-bottom: 1px solid #f0f0f0;
    }

    .culmex-suggestion-item:hover {
        background: #f0f4ff;
        color: #1e3060;
    }

    /* Checkboxes */
    .culmex-checkboxes {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-top: 0.25rem;
    }

    /* File input */
    .culmex-file-input {
        display: block;
        width: 100%;
        font-size: 0.875rem;
        color: #555;
        cursor: pointer;
    }

    .culmex-file-input::file-selector-button {
        margin-right: 1rem;
        padding: 0.4rem 1rem;
        background: #fff;
        border: 1px solid #1e3060;
        color: #1e3060;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        cursor: pointer;
    }

    .culmex-file-input::file-selector-button:hover {
        background: #1e3060;
        color: #fff;
    }

    /* Native checkbox */
    .culmex-native-checkbox {
        width: 1rem;
        height: 1rem;
        margin-top: 0.15rem;
        flex-shrink: 0;
        accent-color: #1e3060;
        cursor: pointer;
    }

    /* Consent */
    .culmex-consent-row {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .culmex-consent-text {
        flex: 1;
        font-size: 0.875rem;
    }

    .culmex-consent-title {
        font-weight: 600;
        color: #1e3060;
    }

    .culmex-link {
        color: #1e3060;
        text-decoration: underline;
    }

    .culmex-link:hover {
        text-decoration: none;
    }

    /* Buttons */
    .culmex-btn-primary {
        display: inline-block;
        padding: 0.7rem 2.5rem;
        background: #1e3060;
        color: #fff;
        font-family: inherit;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        border: 2px solid #1e3060;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
    }

    .culmex-btn-primary:hover:not(:disabled) {
        background: #fff;
        color: #1e3060;
    }

    .culmex-btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .culmex-btn-outline {
        display: inline-block;
        padding: 0.7rem 2.5rem;
        background: #fff;
        color: #1e3060;
        font-family: inherit;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        border: 2px solid #1e3060;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
    }

    .culmex-btn-outline:hover {
        background: #1e3060;
        color: #fff;
    }

    .culmex-submit-row {
        display: flex;
        justify-content: center;
        padding-top: 0.5rem;
    }

    /* Success state */
    .culmex-success {
        padding: 3rem 2rem;
        text-align: center;
    }

    .culmex-success-icon {
        width: 4rem;
        height: 4rem;
        margin: 0 auto 1rem;
        color: #1e3060;
    }

    .culmex-success-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1e3060;
        margin: 0 0 0.75rem;
    }

    .culmex-success-text {
        color: #555;
        margin: 0 0 1.5rem;
    }

    /* Responsive */
    @media (max-width: 540px) {
        .culmex-grid {
            grid-template-columns: 1fr;
        }

        .culmex-col-2 {
            grid-column: span 1;
        }
    }
</style>
